import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const WxCopilotLayoutContext = createContext();

export function WxCopilotLayoutProvider({ children }) {
  // Read cookie at mount so isPanelOpen is correct before the UMD's Zustand store hydrates.
  const [isPanelOpen, setIsPanelOpen] = useState(
    () =>
      document.cookie.match(/(?:^|;\s*)fountain_cue_panel=([^;]*)/)?.[1] ===
      '1',
  );
  const [isWxCopilotReady, setIsWxCopilotReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = null;

    const tryAttach = () => {
      if (cancelled || unsubscribe) return !!unsubscribe;
      const api = window.WxCopilotComponent;
      if (!api?.subscribePanelState || !api.getIsPanelOpen) {
        return false;
      }
      unsubscribe = api.subscribePanelState(next => {
        setIsPanelOpen(next);
      });
      // Being subscribed is enough to be "ready": any future toggle corrects
      // isPanelOpen via the subscription, and the cookie already seeded the
      // initial value. Without this, the plain-poll success path would leave
      // readiness waiting on the :rendered event or the 2s fallback.
      setIsWxCopilotReady(true);
      return true;
    };

    const markReady = () => {
      if (cancelled) return;
      tryAttach();
      setIsWxCopilotReady(true);
    };

    const isAlreadyRendered =
      window.wxCopilotStateInternal?.rendered instanceof Map &&
      window.wxCopilotStateInternal.rendered.size > 0;

    if (isAlreadyRendered) {
      // SPA navigation: UMD store is already hydrated, safe to read current state.
      const api = window.WxCopilotComponent;
      if (api?.getIsPanelOpen) setIsPanelOpen(api.getIsPanelOpen());
      tryAttach();
      setIsWxCopilotReady(true);
      return () => {
        cancelled = true;
        if (unsubscribe) unsubscribe();
      };
    }

    const pollId = setInterval(() => {
      if (tryAttach()) {
        clearInterval(pollId);
      }
    }, 200);

    const handlePanelEvent = event => {
      const state = event.detail?.state;
      tryAttach();
      if (unsubscribe) return;
      setIsPanelOpen(state === 'sidebar');
      setIsWxCopilotReady(true);
    };
    window.addEventListener('wx-copilot-panel', handlePanelEvent);

    const onRendered = () => {
      markReady();
      clearInterval(pollId);
    };
    window.addEventListener('WxCopilotComponent:rendered', onRendered, {
      once: true,
    });

    // Safety net: never block Intercom forever if the UMD fails to load.
    const fallbackTimer = setTimeout(() => {
      if (!cancelled) {
        setIsWxCopilotReady(true);
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      clearTimeout(fallbackTimer);
      window.removeEventListener('wx-copilot-panel', handlePanelEvent);
      window.removeEventListener('WxCopilotComponent:rendered', onRendered);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    const offset = isPanelOpen ? '400px' : '0px';
    const root = document.documentElement;
    root?.style?.setProperty('--wx-copilot-panel-offset', offset);
    return () => {
      root?.style?.removeProperty('--wx-copilot-panel-offset');
    };
  }, [isPanelOpen]);

  const value = useMemo(
    () => ({
      isPanelOpen,
      setIsPanelOpen,
      isWxCopilotReady,
    }),
    [isPanelOpen, isWxCopilotReady],
  );

  return (
    <WxCopilotLayoutContext.Provider value={value}>
      {children}
    </WxCopilotLayoutContext.Provider>
  );
}

WxCopilotLayoutProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const defaultValue = {
  isPanelOpen: false,
  setIsPanelOpen: () => {},
  isWxCopilotReady: false,
};

export function useWxCopilotLayout() {
  const context = useContext(WxCopilotLayoutContext);
  return context || defaultValue;
}
